import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Button,
  Center,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import { OrganizationProfileImage } from '@/components/OrganizationProfileImage';
import { useCurrent } from '@/hooks/useCurrent';
import { logError } from '@/imports/logging/ClientLogger';

import { MessageModal_OrganizationFragment } from './graphql-types/MessageModal_OrganizationFragment';
import {
  MessageModal_createChat,
  MessageModal_createChatVariables,
} from './graphql-types/MessageModal_createChat';

const CREATE_CHAT = gql`
  mutation MessageModal_createChat($input: CreateChatWithOrganization!) {
    createChatWithOrganization(input: $input) {
      streamChannelId
    }
  }
`;

const ORGANIZATION_FRAGMENT = gql`
  fragment MessageModal_OrganizationFragment on Organization {
    _id
    displayName
    ...OrganizationProfileImage_OrganizationFragment
  }
  ${OrganizationProfileImage.organizationFragment}
`;

export const MessageModal: React.FC<{
  isOpen: boolean;
  organization: MessageModal_OrganizationFragment;
  onClose: () => void;
}> & { organizationFragment: DocumentNode } = ({
  isOpen,
  organization,
  onClose,
}) => {
  const toast = useToast();
  const current = useCurrent();

  const [message, setMessage] = React.useState('');

  const [createChat, createChatResult] = useMutation<
    MessageModal_createChat,
    MessageModal_createChatVariables
  >(CREATE_CHAT);

  React.useEffect(() => {
    setMessage('');
  }, [isOpen]);

  const handleClick = async (): Promise<void> => {
    try {
      await createChat({
        variables: {
          input: {
            organizationId: organization._id,
            userId: current.user?._id ?? '',
            message,
          },
        },
      });

      toast({
        title: `Your message was sent to ${organization.displayName}!`,
        status: 'success',
      });
      onClose();
    } catch (err) {
      toast({
        title: 'Failed to send message. Please try again.',
        status: 'error',
      });
      logError(err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader as={Center} flexDir="column" py="6">
          <OrganizationProfileImage organization={organization} mb="5" />

          <Heading size="md">Message {organization.displayName}</Heading>

          <Text color="gray.600" fontWeight="normal">
            This message will be sent to {organization.displayName} Admins.
          </Text>
        </ModalHeader>

        <ModalCloseButton />
        <ModalBody>
          <Textarea
            h={48}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Your message..."
          />
        </ModalBody>

        <ModalFooter>
          <Button
            variant="outline"
            colorScheme="gray"
            mr="3"
            onClick={onClose}
            isDisabled={createChatResult.loading}
          >
            Cancel
          </Button>
          <Button
            isDisabled={!message}
            onClick={handleClick}
            isLoading={createChatResult.loading}
          >
            Send Message
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

MessageModal.organizationFragment = ORGANIZATION_FRAGMENT;
